import { createBrowserInspector } from "@statelyai/inspect";
import { Ref } from "vue";
import { ActorRefFrom, AnyStateMachine, EventFromLogic, SnapshotFrom } from "xstate-5";
import { useActor } from "@xstate-5/vue";
import { useSharedComposable } from "vue-app/composables/useSharedComposable";
import { Router } from "vue-router";
import { Route } from "node_modules/@sentry/vue/build/types/browserTracingIntegration";

export { inspector, xlog, whichXStateInspectorRun };

const browserInspector = createBrowserInspector({
  autoStart: false,
});

function inspector() {
  if (whichXStateInspectorRun() === "xstate5") {
    browserInspector.start();
    return browserInspector;
  }
  return { inspect: undefined };
}

function whichXStateInspectorRun(): "xstate4" | "xstate5" | null {
  if (window.location.search.includes("xstate5")) {
    sessionStorage.setItem("xstate5", "true");
    sessionStorage.removeItem("xstate4");
    return "xstate5";
  }

  if (window.location.search.includes("xstate4")) {
    sessionStorage.setItem("xstate4", "true");
    sessionStorage.removeItem("xstate5");
    return "xstate4";
  }

  if (!window.location.search.includes("xstate5") && !window.location.search.includes("xstate4")) {
    if (sessionStorage.getItem("xstate5")) {
      return "xstate5";
    }
    if (sessionStorage.getItem("xstate4")) {
      return "xstate4";
    }
  }
  return null;
}

/**
 * ?xlog in url to enable xstate logs
 */
function isXStateDebugEnabled() {
  const urlParams = new URLSearchParams(window.location.search);
  if (sessionStorage.getItem("xlog") === "true") {
    return true;
  }
  if (urlParams.has("xlog")) {
    sessionStorage.setItem("xlog", "true");
    return true;
  }
  return false;
}

function xlog(data: {
  label: string;
  type?: "action" | "guard" | undefined;
  context?: Record<string, any>;
  event?: Record<string, any>;
  params?: unknown;
}) {
  if (!isXStateDebugEnabled()) {
    return;
  }

  try {
    if (data.type === "action") {
      console.groupCollapsed(`%c${data.label}`, "color: white; background-color: blue; padding: 2px");
    } else if (data.type === "guard") {
      console.groupCollapsed(`%c${data.label}`, "color: white; background-color: green; padding: 2px");
    } else {
      console.groupCollapsed(`%c${data.label}`, "color: white; background-color: black; padding: 2px");
    }

    if (data.context) {
      console.groupCollapsed("Context");
      console.log(JSON.stringify(data.context));
      console.groupEnd();
    }

    if (data.event) {
      console.groupCollapsed(`Event ["${data.event.type}"]`);
      console.log(JSON.stringify(data.event));
      console.groupEnd();
    }

    if (data.params) {
      console.groupCollapsed(`Params`);
      console.log(JSON.stringify(data.params));
      console.groupEnd();
    }

    console.groupCollapsed("Stack Trace");
    console.trace(`${data.label}`);
    console.groupEnd();
    console.groupEnd();
  } catch {}
}

export const createSharedMachine = <TMachine extends AnyStateMachine>() => {
  return useSharedComposable(useActor) as unknown as (
    machine: TMachine,
    options?: {
      inspect: ReturnType<typeof inspector>["inspect"];
      input: {
        [key: string]: unknown;
        route: Route;
        router: Router;
      };
    }
  ) => {
    snapshot: Ref<SnapshotFrom<TMachine>>;
    send: (event: EventFromLogic<TMachine>) => void;
    actorRef: ActorRefFrom<TMachine>;
  };
};
